import { useCallback, useLayoutEffect, useMemo, useState, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { ToolViewContext } from '../shared/tool-navigation';

/** Keep React and native file inputs alive while exposing only the active tool to the DOM. */
export function CachedToolView({ viewKey, active, location, containerRef, onLocationChange, children }: {
  viewKey: string;
  active: boolean;
  location: string;
  containerRef: RefObject<HTMLDivElement | null>;
  onLocationChange: (viewKey: string, href: string) => void;
  children: ReactNode;
}) {
  const [host] = useState(() => {
    const element = document.createElement('div');
    element.className = 'halo-cached-tool-view';
    element.dataset.toolView = viewKey;
    return element;
  });
  useLayoutEffect(() => {
    if (active) containerRef.current?.appendChild(host);
    else host.remove();
    return () => host.remove();
  }, [active, containerRef, host]);
  const replaceLocation = useCallback((href: string) => onLocationChange(viewKey, href), [onLocationChange, viewKey]);
  const value = useMemo(() => ({ active, location, replaceLocation }), [active, location, replaceLocation]);
  return createPortal(<ToolViewContext.Provider value={value}>{children}</ToolViewContext.Provider>, host, viewKey);
}
